import React, {useEffect, useState} from 'react'
import DataTable, {TableColumn} from 'react-data-table-component'
import {FaDownload, FaRedo} from 'react-icons/fa'
import ActionDropdown from '../modules/accounts/components/CRUDWrapper/ActionDropDown'
import {Link} from 'react-router-dom'
import get from 'lodash/get'
import Loader from './Loader'
import {useReduxAuth} from '../../auth'
import PhoneLink from './common/PhoneLink'
import {RoleGuard} from './permissions/RoleGuard'
import {USER_ROLES} from '../../../constants/StatusConstants'
import {getBPSeverityLabel} from '../helper/helperFunction'
import {formatToUSDateTimeShort, formatBillingCycleDateToUser} from '../helper/dateTimeHelpers'
import DownloadPdfButton from './common/buttons/DownloadPDFButton'
import {FaXmark} from 'react-icons/fa6'
import PrimaryButton from './common/buttons/PrimaryButton'
import {statusColorUtilities as utilities} from '../../../utitlities/statusColors'
import moment from 'moment'

interface DataTableListProps {
  title?: string
  showRefreshButton?: boolean
  showHeader?: boolean
  columns: any[]
  data: any[]
  actions?: any[]
  getRowActions?: (row: any) => any[]
  onAction?: (action: {name: string; title: string}, row: any) => void
  showAddButton?: boolean
  onAddClick?: () => void
  onReload?: () => void
  isLoading?: boolean
  handleStatusToggle?: (row: any) => void
  handleManualSubscription?: (row: any) => void
  requiredPermissionToUpdateStatus?: string
  showExportButton?: boolean
  showOnlySearch?: boolean
  onExportClick?: () => void
  allowedRoles?: number[]
  showPatientFilters?: boolean
  addButtonText?: string
  customInputWidth?: string // Optional prop for custom search input width
  patientFilterValues?: {
    timeSpent: string
    lastReading: string
    readingCount: string
  }
  onPatientFilterChange?: (field: string, value: string) => void
  // Custom filter props
  showCustomFilters?: boolean
  customFilters?: {
    name: string
    key: string
    type: 'search' | 'select'
    placeholder?: string
    options?: {value: string; label: string}[]
    value: string
    displayValueCallback?: (value: string) => string
  }[]
  onCustomFilterChange?: (key: string, value: string) => void
  onClearAllCustomFilters?: () => void
  loadingRowId?: number | null
  footerNote?: React.ReactNode
  onDetailedReportDownload?: (row: any) => void
  downloadingDetailedReportId?: number | string | null
}

const DataTableList = ({
  title,
  showRefreshButton = false,
  showHeader = true,
  columns,
  data,
  actions,
  getRowActions,
  onAction,
  showAddButton = true,
  showOnlySearch = false,
  onAddClick,
  onReload,
  isLoading = false,
  handleStatusToggle,
  handleManualSubscription,
  requiredPermissionToUpdateStatus,
  allowedRoles = [],
  showPatientFilters = false,
  patientFilterValues,
  onPatientFilterChange,
  showExportButton = false,
  onExportClick,
  // Custom filter props
  showCustomFilters = false,
  customFilters = [],
  addButtonText = 'Add',
  onCustomFilterChange,
  onClearAllCustomFilters,
  customInputWidth, // Optional prop for custom search input width
  loadingRowId,
  footerNote,
  onDetailedReportDownload,
  downloadingDetailedReportId,
}: DataTableListProps) => {
  const [selectedColumn, setSelectedColumn] = useState(columns.map((column) => column.name))
  const {currentUser} = useReduxAuth()
  useEffect(() => {
    setTimeout(() => {
      document.querySelectorAll("[draggable='true']").forEach((el) => {
        el.setAttribute('draggable', 'false')
      })
    }, 500)
  }, [])

  useEffect(() => {
    setSelectedColumn(columns.map((col) => col.name))
  }, [columns])

  const renderActions = (row: any) => {
    const onActionPerformed = (actionType: any) => {
      onAction?.(actionType, row)
    }

    // Show loading state if this row is being processed
    if (loadingRowId === row.id) {
      return (
        <div style={{position: 'relative', display: 'inline-block'}}>
          <button
            style={{
              background: 'none',
              border: 'none',
              cursor: 'not-allowed',
              padding: '5px',
            }}
            disabled
          >
            <i className='mdi mdi-loading mdi-spin' style={{fontSize: '20px', color: '#007bff'}} />
          </button>
        </div>
      )
    }

    // Dynamically filter actions based on row data
    const effectiveActions = getRowActions ? getRowActions(row) : actions ?? []
    const rowActions = effectiveActions?.filter((action) => {
      // For billing personnel, only show delete action
      if (row.role_id === USER_ROLES.BILLING_PERSONNEL) {
        if (action.name === 'CANCEL_INVITE') return row.status === 'pending';
        if (action.name === 'DELETE') return true;
      }

      // Never show cancel invite for non-billing personnel
      if (action.name === 'CANCEL_INVITE') return false;

      // For nurses, apply existing logic
      if (action.name === 'RESEND_EMAIL_TO_STAFF') {
        // resend email action should only be shown if staff is active and email is not verified
        return row.status === 1 && row.email_verified_at == null // Only show if staff is active
      }

      if (action.name === 'RESEND_EMAIL_TO_DOCTOR') {
        // resend email action should only be shown if doctor is active and email is not verified
        return row.status === 1 && row.email_verified_at == null
      }

      // For doctors - show activate/deactivate based on status
      if (action.name === 'ACTIVATE') {
        // Show activate only if doctor is inactive (status === 0)
        return row.status === 0
      }

      if (action.name === 'DEACTIVATE') {
        // Show deactivate only if doctor is active (status === 1)
        return row.status === 1
      }

      // For device actions - check if device is assigned to a patient
      if (action.name === 'ASSIGN_DEVICE' || action.name === 'UNASSIGN_DEVICE') {
        // Check if device is assigned to a patient from DoctorDeviceListingPage data structure
        let isAssignedToPatient = false

        // Check Device.AllocatedDevice structure (for admin device listing)
        if (row?.Device?.AllocatedDevice?.[0]) {
          const deviceAllocation = row.Device.AllocatedDevice[0]
          isAssignedToPatient = deviceAllocation && !deviceAllocation.devices_de_allocation_date
        }
        // Check DoctorAllocatedDevices structure (for doctor device listing)
        else if (row?.DoctorAllocatedDevices) {
          isAssignedToPatient = row.DoctorAllocatedDevices.some(
            (alloc: any) => alloc.returned_at === null
          )
        }
        // Show ASSIGN_DEVICE only if device is NOT assigned to any patient
        if (action.name === 'ASSIGN_DEVICE') {
          return !isAssignedToPatient
        }

        // Show UNASSIGN_DEVICE only if device IS assigned to a patient
        if (action.name === 'UNASSIGN_DEVICE') {
          return isAssignedToPatient
        }
      }

      return true // Keep other actions
    })

    return (
      <div style={{position: 'relative', display: 'inline-block'}}>
        <ActionDropdown actions={rowActions} onActionPerformed={onActionPerformed} />
      </div>
    )
  }

  const getValue = (item: any, header: any, secondaryKeyName = null) => {
    if (typeof header.selector === 'function') {
      const value = header.selector(item)
      return value ?? '-'
    }

    let value = get(item, secondaryKeyName ? secondaryKeyName : header.key, '-')

    return value ?? '-'
  }


  const renderCell = (row: any, column: any) => {
    switch (column.key) {
      case 'patient':
        const isBiller = currentUser?.role_id === USER_ROLES.BILLING_PERSONNEL
        const patientDob = row?.Patient?.dob
          ? moment(row.Patient.dob).format('MM/DD/YYYY')
          : null
        const truncateStyle: React.CSSProperties = {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        }
        return isBiller ? (
          <div style={{overflow: 'hidden', maxWidth: '100%'}}>
            <span className="fw-semibold d-block" style={truncateStyle} title={row?.full_name}>
              {row?.full_name}
            </span>
            {patientDob && (
              <small className="text-muted d-block" style={{fontSize: '0.75rem'}}>
                DOB: {patientDob}
              </small>
            )}
          </div>
        ) : (
          <div style={{overflow: 'hidden', maxWidth: '100%'}}>
            <Link
              to={`/patient-details/${row?.uuid}/billing`}
              className='fw-semibold d-block'
              style={truncateStyle}
              title={row?.full_name}
            >
              {row?.full_name}
            </Link>
            {patientDob && (
              <small className="text-muted d-block" style={{fontSize: '0.75rem'}}>
                DOB: {patientDob}
              </small>
            )}
          </div>
        )

      case 'downloadPdf':
        return <DownloadPdfButton url={row.downloadPdf} allowedRoles={column.allowedRoles} />

      case 'detailed_report': {
        const billingCycleId = row?.billing_cycle_id
        const isUnavailable = billingCycleId === undefined || billingCycleId === null
        const isDownloading =
          !isUnavailable && String(downloadingDetailedReportId) === String(billingCycleId)
        const label = isUnavailable
          ? 'Detailed report unavailable'
          : isDownloading
          ? 'Downloading detailed report'
          : 'Download detailed report'

        return (
          <RoleGuard allowedRoles={column.allowedRoles}>
            <div className='d-flex justify-content-center w-100'>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light-primary'
                style={{width: '34px', height: '34px'}}
                onClick={() => onDetailedReportDownload?.(row)}
                disabled={isUnavailable || isDownloading || !onDetailedReportDownload}
                aria-label={label}
                title={label}
              >
                {isDownloading ? (
                  <span className='spinner-border spinner-border-sm' aria-hidden='true' />
                ) : (
                  <FaDownload aria-hidden='true' />
                )}
              </button>
            </div>
          </RoleGuard>
        )
      }

      case 'redirected_to':
        const linkValue = get(row, column.secondaryKeyName, '-')
        const isLinkValueDisabled = linkValue === '-'
        const isStaffOrBiller = row.role_id &&
          [USER_ROLES.NURSE, USER_ROLES.BILLING_PERSONNEL].includes(row.role_id)

        const isPending =
          isStaffOrBiller &&
          (
            (row.role_id === USER_ROLES.BILLING_PERSONNEL && row.status === 'pending') ||
            (row.role_id === USER_ROLES.NURSE && row.status === 1 && !row.email_verified_at)
          )

        const isLinkDisabled = isLinkValueDisabled || isPending
        return !isLinkDisabled ? (
          <span
            className='text-start  max-width text-truncate'
            title={row.full_name || row?.Device?.AllocatedDevice[0]?.Patient?.full_name}
          >
            <Link
              to={`${column.redirected_to}/${
                row?.uuid || row?.Device?.AllocatedDevice[0]?.Patient?.uuid
              }${column.redirected_to === '/patient-details' ? '/health-metrics' : ''}`}
              className='text-link'
            >
              {row.full_name || row?.Device?.AllocatedDevice[0]?.Patient?.full_name}
            </Link>
          </span>
        ) : (
          <span>{linkValue}</span>
        )

      case 'gender':
        return (
          <span
            style={{
              padding: '5px 10px',
              borderRadius: '5px',
              backgroundColor: utilities.getStatusColor(row.gender || 'N/A'),
              color:
                row.gender === null ? '#7239ea' : row.gender === 'male' ? '#006064' : '#880e4f',
              textTransform: 'capitalize',
            }}
          >
            {row.gender || 'N/A'}
          </span>
        )

      case 'role_id': {
        const baseBadgeStyle = {
          display: 'inline-block',
          padding: '5px 16px',
          borderRadius: '20px',
          fontSize: '0.9rem',
          fontWeight: '600',
          textAlign: 'center' as const,
          whiteSpace: 'nowrap' as const,
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          border: '1px solid',
          letterSpacing: '0.5px',
          minWidth: '90px',
          transition: 'all 0.2s ease',
        }

        switch (row.role_id) {
          case USER_ROLES.NURSE:
            return (
              <span
                style={{
                  ...baseBadgeStyle,
                  // backgroundColor: '#e8f5e8',
                  color: 'rgb(40, 167, 69)',
                  border: '1px solid rgb(40, 167, 69)',
                }}
                title='Nurse Staff Member'
              >
                Staff
              </span>
            )
          case USER_ROLES.BILLING_PERSONNEL:
            return (
              <span
                style={{
                  ...baseBadgeStyle,
                  // backgroundColor: '#e3f2fd',
                  color: '#1565c0',
                  border: '1px solid #64b5f6',
                }}
                title='Billing Personnel Staff Member'
              >
                Biller
              </span>
            )
          default:
            return (
              <span
                style={{
                  ...baseBadgeStyle,
                  backgroundColor: '#f5f5f5',
                  color: '#757575',
                  borderColor: '#bdbdbd',
                }}
                title='Unknown Staff Role'
              >
                ❓ Unknown
              </span>
            )
        }
      }

      case 'phone_number':
        return row?.phone_number ? (
          <PhoneLink className='text-start mw-180 text-truncate' phone={row?.phone_number} />
        ) : (
          '-'
        )

      case 'description':
        return <span title={row?.description}>{row?.description || 'N/A'}</span>

      case 'date':
        // Handle date column with potential manual badge
        const dateValue = formatToUSDateTimeShort(row.from)
        if (row.tracking_type?.toLowerCase() === 'manual') {
          return (
            <span>
              {dateValue} <span className='badge bg-warning text-dark ms-1'>Manual</span>
            </span>
          )
        }
        return dateValue

      case 'status': {
        // only for invited billers
        if (row.status === 'pending' || row.status === 'accepted') {
          return (
            <span className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill small fw-bold text-capitalize text-nowrap"
              style={{ border: '1px solid #ffc107', color: '#ffc107' }}>
              Pending
            </span>
          )
        }
        // For billing personnel, access control is managed through DoctorBillingPersonnel junction table
        // Use the has_access field to determine their actual access status
        // For nurses, use the user status field as before
        const isBillingPersonnel = row.role_id === USER_ROLES.BILLING_PERSONNEL
        // const isActive = isBillingPersonnel ? Boolean(row.has_access) : row.status === 1 && row.email_verified_at
        
        // temporary adding to sync biller status on admin side 
        const isAdminView = currentUser?.role_id === USER_ROLES.ADMIN 
        const isActive = isBillingPersonnel && isAdminView
          ? (row.status === 1 && Boolean(row.email_verified_at))
          : isBillingPersonnel
            ? Boolean(row.has_access)
            : (row.status === 1 && Boolean(row.email_verified_at))
        const { statusText, color } = isActive
          ? { statusText: 'Active', color: '#28a745' }
          : row.status === 1 && !row.email_verified_at
            ? { statusText: 'Pending', color: '#ffc107' }
            : { statusText: 'Inactive', color: '#dc3545' }

        return (
          <div
            onClick={() => handleStatusToggle?.(row)}
            style={{display: 'inline-block', cursor: 'pointer'}}
          >
            <span
              style={{
                border: `1px solid ${color}`,
                color,
              }}
              className={`d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill small fw-bold text-capitalize text-nowrap`}
            >
              {statusText}
            </span>
          </div>
        )
      }

      case 'subscription_status': {
        let statusText = row.subscription_status
        let color, bg

        // Format status text
        if (row.subscription_status === 'past_due') {
          statusText = 'Past Due'
        } else if (row.subscription_status === 'trial') {
          statusText = `Trial (${row.trial_limit})`
        } else {
          statusText = row.subscription_status
        }

        // TODO::Have a helper to get color by status used everywhere and pick from defined colors
        if (row.subscription_status === 'active') {
          color = '#28a745'
        } else if (row.subscription_status === 'inactive') {
          color = '#dc3545'
        } else if (
          row.subscription_status === 'cancelled' ||
          row.subscription_status === 'canceled' ||
          row.subscription_status === 'terminated'
        ) {
          color = '#dc3545'
        } else if (row.subscription_status === 'trial') {
          color = '#006064'
        } else {
          color = 'gray'
        }

        return (
          <div style={{display: 'inline-block'}}>
            <button
              className='border-0 bg-transparent'
              onClick={() => handleManualSubscription?.(row)}
            >
              <span
                style={{
                  border: `1px solid ${color}`,
                  backgroundColor: bg,
                  color,
                }}
                className={`d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill small fw-bold text-capitalize`}
              >
                {statusText}
              </span>
            </button>
          </div>
        )
      }

      case 'allocated_devices_count': {
        const rentalDevices = row.rental_devices_count || 0
        const loanedDevices = row.loaned_devices_count || 0
        const ownedDevices = row.owned_devices_count || 0

        return (
          <span title="Rent - Loaned - Owned">
            <span style={{color: '#28a745', fontWeight: 'bold'}}>{rentalDevices}</span>
            {' - '}
            <span style={{color: 'orange', fontWeight: 'bold'}}>{loanedDevices}</span>
            {' - '}
            <span style={{color: 'var(--bs-primary)', fontWeight: 'bold'}}>{ownedDevices}</span>
          </span>
        )
      }

      case 'patients_count_by_status': {
        return (
          <span>
            <span style={{color: 'var(--bs-primary)', fontWeight: 'bold'}}>
              {row.active_patients_count}
            </span>
            {' - '}
            <span style={{color: 'orange', fontWeight: 'bold'}}>{row.inactive_patients_count}</span>
          </span>
        )
      }

      case 'patient_status_in_billing': {
        // read the real status flag
        const isActive = row.status === 1

        // colors you'll use
        const color = isActive ? '#28a745' : '#dc3545'
        const bg = isActive ? 'rgba(40,167,69,0.1)' : 'rgba(220,53,69,0.1)'

        return (
          <span
            style={{
              border: `1px solid ${color}`,
              backgroundColor: bg,
              color,
            }}
            className={`d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill small fw-bold text-capitalize`}
          >
            {isActive ? 'Active' : 'Inactive'}
          </span>
        )
      }

      case 'dob':
        return row?.Patient?.dob
          ? moment(row.Patient.dob).format('MM/DD/YYYY')
          : '---'


      case 'bp_classification':
        return (
          <span
            className='badge badge-lg'
            style={{
              backgroundColor: utilities.getSeverityColor(row?.bp_category),
            }}
          >
            {getBPSeverityLabel(row?.bp_category)}
          </span>
        )

      case 'request_status':
        return (
          <span
            style={{
              padding: '5px 10px',
              borderRadius: '5px',
              backgroundColor: utilities.getRequestedStatusColor(row.status),
              color: 'white',
              fontSize: '12px',
              textTransform: 'capitalize',
            }}
          >
            {row.status === 0 ? 'Pending' : row.status === 1 ? 'Accepted' : 'Rejected'}
          </span>
        )

      case 'priority':
        return (
          <span
            style={{
              padding: '5px 10px',
              borderRadius: '5px',
              backgroundColor: utilities.getStatusColor(row.priority),
              color: 'white',
              fontSize: '12px',
              textTransform: 'capitalize',
            }}
            onClick={() => onAction?.({name: 'Priority', title: 'Priority'}, row)}
          >
            {row.priority}
          </span>
        )

      case 'notes':
        const note = row?.TelemetryData?.[2]?.measured_value || 'N/A'
        return (
          <div className='d-flex justify-content-between align-items-center'>
            {/* Note Text */}
            <span
              title={note}
              className='text-truncate'
              style={{maxWidth: '160px', marginRight: '1rem'}}
            >
              {note}
            </span>

            {/* Icon Actions */}
            <div
              className='d-flex align-items-center gap-2'
              style={{fontSize: '14px', color: '#495057'}}
            >
              <i
                className='bi bi-pencil-square'
                style={{cursor: 'pointer'}}
                onClick={() => onAction?.({name: 'EditNote', title: 'Edit Note'}, row)}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#0d6efd')} // Bootstrap primary
                onMouseLeave={(e) => (e.currentTarget.style.color = '#495057')} // dark gray
              />
              <span>/</span>
              <i
                className='bi bi-trash'
                style={{cursor: 'pointer'}}
                onClick={() => onAction?.({name: 'DeleteNote', title: 'Delete Note'}, row)}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#dc3545')} // Bootstrap danger
                onMouseLeave={(e) => (e.currentTarget.style.color = '#495057')}
              />
            </div>
          </div>
        )

      case 'settings':
        return renderActions(row)

      case 'actions':
        return (
          <RoleGuard allowedRoles={column.allowedRoles}>
            <div className='d-flex align-items-center gap-3'>
              {/* Edit Button */}
              <button
                className='d-flex align-items-center justify-content-center p-2'
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  backgroundColor: 'transparent',
                }}
                onClick={() => onAction?.({name: 'Edit', title: 'Edit'}, row)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--bs-primary)'
                  e.currentTarget.style.backgroundColor = 'rgba(13, 110, 253, 0.1)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--bs-gray-600)'
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
                aria-label='Edit entry'
              >
                <i className='bi bi-pencil-square' />
                <div
                  className='position-absolute bottom-100 start-50 translate-middle-x mb-2 px-2 py-1'
                  style={{
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    opacity: 0,
                    transition: 'opacity 0.2s ease',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Edit
                </div>
              </button>

              {/* Delete Button */}
              <button
                className='d-flex align-items-center justify-content-center p-2'
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  backgroundColor: 'transparent',
                }}
                onClick={(e) => {
                    onAction?.({name: 'Delete', title: 'Delete'}, row)
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--bs-danger)'
                  e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.1)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--bs-gray-600)'
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
                aria-label='Delete entry'
              >
                <i className='bi bi-trash' />
                <div
                  className='position-absolute bottom-100 start-50 translate-middle-x mb-2 px-2 py-1'
                  style={{
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    opacity: 0,
                    transition: 'opacity 0.2s ease',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Delete
                </div>
              </button>
            </div>
          </RoleGuard>
        )

      case 'cpt_99445': {
        const eligible = row?.cpt_99445
        const color = eligible ? '#e67e22' : '#dc3545'
        const bg = eligible ? 'rgba(230,126,34,0.1)' : 'rgba(220,53,69,0.1)'

        return (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '16px',
              border: `1px solid ${color}`,
              backgroundColor: bg,
              color,
              fontSize: '0.75rem',
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            {eligible ? 'Eligible*' : 'Ineligible'}
          </span>
        )
      }

      case 'cpt_99470': {
        const eligible = row?.cpt_99470
        const color = eligible ? '#28a745' : '#dc3545'
        const bg = eligible ? 'rgba(40,167,69,0.1)' : 'rgba(220,53,69,0.1)'

        return (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '16px',
              border: `1px solid ${color}`,
              backgroundColor: bg,
              color,
              fontSize: '0.75rem',
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            {eligible ? 'Eligible' : 'Ineligible'}
          </span>
        )
      }

      case 'cpt_99453': {
        const eligible = row?.cpt_99453

        const color = eligible ? '#28a745' : '#dc3545'
        const bg = eligible ? 'rgba(40,167,69,0.1)' : 'rgba(220,53,69,0.1)'

        return (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '16px',
              border: `1px solid ${color}`,
              backgroundColor: bg,
              color,
              fontSize: '0.75rem',
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            {eligible ? 'Eligible' : 'Ineligible'}
          </span>
        )
      }

      case 'cpt_99454': {
        const eligible = row?.cpt_99454
        const color = eligible ? '#28a745' : '#dc3545'
        const bg = eligible ? 'rgba(40,167,69,0.1)' : 'rgba(220,53,69,0.1)'

        return (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '16px',
              border: `1px solid ${color}`,
              backgroundColor: bg,
              color,
              fontSize: '0.75rem',
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            {eligible ? 'Eligible' : 'Ineligible'}
          </span>
        )
      }

      case 'cpt_99457': {
        const eligible = row?.cpt_99457
        const color = eligible ? '#28a745' : '#dc3545'
        const bg = eligible ? 'rgba(40,167,69,0.1)' : 'rgba(220,53,69,0.1)'

        return (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '16px',
              border: `1px solid ${color}`,
              backgroundColor: bg,
              color,
              fontSize: '0.75rem',
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            {eligible ? 'Eligible' : 'Ineligible'}
          </span>
        )
      }

      case 'cpt_99458': {
        const cpt_99458_value = row?.cpt_99458
        const isEligible = row?.cpt_99458 !== 'Ineligible' && row?.cpt_99458 !== 0
        const color = isEligible ? '#28a745' : '#dc3545'
        const bg = isEligible ? 'rgba(40,167,69,0.1)' : 'rgba(220,53,69,0.1)'

        return (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '16px',
              border: `1px solid ${color}`,
              backgroundColor: bg,
              color,
              fontSize: '0.75rem',
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            {isEligible
              ? `${cpt_99458_value} unit${cpt_99458_value > 1 ? 's' : ''}`
              : 'Ineligible'}
          </span>
        )
      }

      case 'assigned_to_patient_icon': {
        const isAssigned = getValue(row, column)
        return (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: isAssigned ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)',
              color: isAssigned ? '#28a745' : '#dc3545',
              fontSize: '14px',
            }}
            title={isAssigned ? 'Assigned to Patient' : 'Not Assigned to Patient'}
          >
            {isAssigned ? '✓' : '✗'}
          </span>
        )
      }

      case 'device_allocation_status': {
        // Import the AllocationStatusPill component
        const allocationData = getValue(row, column)

        // If the returned value is an object with status property, render the pill
        if (allocationData && typeof allocationData === 'object' && 'status' in allocationData) {
          const AllocationStatusPill = React.lazy(() => import('./device/AllocationStatusPill'))
          return (
            <React.Suspense fallback={<span>Loading...</span>}>
              <AllocationStatusPill
                deviceId={allocationData.deviceId}
                status={allocationData.status}
                doctorAllocatedDeviceId={allocationData.doctorAllocatedDeviceId}
                doctorId={allocationData.doctorId}
                onStatusChange={() => {
                  if (onReload) {
                    onReload()
                  }
                }}
              />
            </React.Suspense>
          )
        }

        // Fallback to text if data is missing
        return typeof allocationData === 'string' ? allocationData : 'Unknown'
      }

      case 'billing_cycle_status': {
        // Get billing cycle status from the selector
        const status = getValue(row, column)

        // Declare variables
        let dateRange = ''

        // If status is a date range (like "Jan 1 - Jan 31"), display it directly
        if (status && status !== '-' && status.includes(' - ')) {
          return (
            <div className='py-2 d-flex flex-column align-items-center'>
              <p className='text-center'>{status}</p>
            </div>
          )
        }

        switch (status) {
          case 'No Cycle':
            dateRange = ''
            break
          case 'Active Cycle':
            dateRange = `${formatBillingCycleDateToUser(
              row.BillingCycle?.cycle_start,
              'MMM D'
            )} - ${formatBillingCycleDateToUser(row?.BillingCycle?.cycle_end, 'MMM D')}`
            break
          case 'Ended Cycle':
            dateRange = `${formatBillingCycleDateToUser(
              row.BillingCycle?.cycle_start,
              'MMM D'
            )} - ${formatBillingCycleDateToUser(row?.BillingCycle?.cycle_end, 'MMM D')}`
            break
          default:
            dateRange = ''
        }

        return (
          <div className='py-2 d-flex flex-column align-items-center'>
            <p className='text-center'>{dateRange}</p>
          </div>
        )
      }

      case 'end_cycle': {
        const value = getValue(row, column)
        return (
          <span
            style={{
              display: 'block',
              padding: '0 5px',
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </span>
        )
      }

      default:
        const value = getValue(row, column)
        return (
          <span
            style={{
              display: 'block',
              padding: '0 5px',
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </span>
        )
    }
  }

  const computedColumns = selectedColumn.length
    ? columns.filter((column) => column.name === 'Actions' || selectedColumn.includes(column.name))
    : columns

  // 10 data pagination getting current page
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  const tableHeaders = [
    {
      name: '#',
      cell: (row: any, index: number) => (
        <span
          style={{
            display: 'block',
            padding: '0 5px',
            textAlign: 'center',
          }}
        >
          {(currentPage - 1) * perPage + index + 1}
        </span>
      ),
      reorder: false,
      width: '50px',
    },
    ...computedColumns.map((col) => ({
      name: col.name,
      cell: (row: any, index: number) => renderCell(row, col),
      reorder: false,
      sortable: col.sortable ?? true,
      width: col.width || 'auto',
      style: col.key === 'description' ? {maxWidth: '710px'} : undefined,
      grow: col.grow !== undefined ? col.grow : !col.width ? 1 : 0,
      selector:
        col.selector ??
        ((row: any) => {
          // fallback selector for sorting if no selector is defined
          const first = col.secondaryKeyName ? row[col.secondaryKeyName] : row[col.key]
          const last = col.tertiaryKeyName ? row[col.tertiaryKeyName] : ''
          return `${first || ''} ${last || ''}`.trim().toLowerCase()
        }),
    })),
  ]

  const handleColumnOrderChange = (nextOrder: TableColumn<any>[]) => {
    return tableHeaders
  }

  return (
    <div className='bg-white border rounded-4' style={{padding: '20px'}}>
      <style>
        {`
          .rdt_Table {
            table-layout: auto !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          .rdt_TableCol {
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
          }
          .rdt_TableCell {
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
          }
          .rdt_TableCell[data-column="description"] {
            max-width: 600px !important;
          }
        `}
      </style>
      {showHeader && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 0px',
            backgroundColor: '#FFFFFF',
            borderRadius: '5px 5px 0 0',
            flexWrap: 'wrap',
            gap: '10px',
          }}
        >
          <div
            className='d-flex d-flex-wrap d-items-center gap-2'
            // style={{display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}
          >
            <h1 className='m-0 fw-bold' style={{margin: 0, fontWeight: 'bold'}}>
              {title}
            </h1>
          </div>

          <div
            className='d-flex d-items-center gap-3'
            // style={{display: 'flex', alignItems: 'center', gap: '15px'}}
          >
            {showPatientFilters && (
              <div className='d-flex gap-3 mb-3 flex-wrap'>
                <div>
                  <label className='form-label'>Last Reading</label>
                  <select
                    className='form-select'
                    value={patientFilterValues?.lastReading || ''}
                    onChange={(e) => onPatientFilterChange?.('lastReading', e.target.value)}
                  >
                    <option value=''>All</option>
                    <option value='<24'>Last 24 Hours</option>
                    <option value='24-48'>24 to 48 Hours Ago</option>
                    <option value='48-72'>48 to 72 Hours Ago</option>
                    <option value='>72'>More than 72 Hours Ago</option>
                    <option value='never'>Never Taken</option>
                  </select>
                </div>
                <div>
                  <label className='form-label'>Care Mins</label>
                  <select
                    className='form-select'
                    value={patientFilterValues?.timeSpent || ''}
                    onChange={(e) => onPatientFilterChange?.('timeSpent', e.target.value)}
                  >
                    <option value=''>All</option>
                    <option value='0-9'>0–9</option>
                    <option value='10-19'>10–19</option>
                    <option value='20-29'>20–29</option>
                    <option value='30-39'>30–39</option>
                    <option value='40-49'>40–49</option>
                    <option value='50-59'>50–59</option>
                    <option value='60'>60+</option>
                  </select>
                </div>
                <div>
                  <label className='form-label'>Reading Count</label>
                  <select
                    className='form-select'
                    value={patientFilterValues?.readingCount || ''}
                    onChange={(e) => onPatientFilterChange?.('readingCount', e.target.value)}
                  >
                    <option value=''>All</option>
                    <option value='0-4'>0–4</option>
                    <option value='5-10'>5–10</option>
                    <option value='11-19'>11–19</option>
                    <option value='20+'>20+</option>
                  </select>
                </div>
              </div>
            )}

            {/* Custom filters */}
            {showCustomFilters && customFilters.length > 0 && (
              <div className='' style={{overflow: 'hidden'}}>
                <div className='d-flex gap-3 flex-wrap' style={{margin: 0}}>
                  {customFilters.map((filter) => (
                    <div key={filter.key} className=''>
                      <div className='d-flex align-items-center mb-1' style={{minHeight: '24px'}}>
                        <label
                          className={`form-label mb-0 text-nowrap text-truncate ${
                            filter.type === 'search'
                              ? filter.value !== ''
                                ? 'fw-bold text-primary'
                                : ''
                              : filter.value !== 'all'
                              ? 'fw-bold text-primary'
                              : ''
                          }`}
                          style={{maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis'}}
                          title={filter.name}
                        >
                          {filter.name}
                        </label>{' '}
                        {/* {(filter.type === 'search'
                          ? filter.value !== ''0
                          : filter.value !== 'all') &&
                          !showOnlySearch && <span className='badge bg-primary ms-1'>Active</span>} */}
                      </div>

                      {filter.type === 'search' ? (
                        <div className='position-relative'>
                          <input
                            type='text'
                            className='form-control pe-5'
                            style={customInputWidth ? {width: customInputWidth} : undefined}
                            value={filter.value}
                            placeholder={
                              filter.placeholder || `Search by ${filter.name.toLowerCase()}`
                            }
                            onChange={(e) =>
                              onCustomFilterChange &&
                              onCustomFilterChange(filter.key, e.target.value)
                            }
                          />
                          {showOnlySearch && filter.value !== '' && (
                            <span className='srch-icon' onClick={onClearAllCustomFilters}>
                              <FaXmark />
                            </span>
                          )}
                        </div>
                      ) : (
                        <select
                          className={`form-select ${
                            filter.value !== 'all' ? 'border-primary' : ''
                          }`}
                          value={filter.value}
                          onChange={(e) =>
                            onCustomFilterChange && onCustomFilterChange(filter.key, e.target.value)
                          }
                        >
                          {filter.options?.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  ))}
                </div>

              </div>
            )}
            {showAddButton && (
              <div className='d-flex align-items-end'>
                <PrimaryButton className='text-nowrap py-2' onClick={onAddClick}>
                  {addButtonText}
                </PrimaryButton>
              </div>
            )}
            {showRefreshButton && (
              <button
                style={{
                  backgroundColor: 'transparenname, imei,t',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9e9e9e',
                  fontSize: '18px',
                }}
                onClick={onReload}
              >
                <FaRedo />
              </button>
            )}
            {showExportButton && (
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9e9e9e',
                  fontSize: '18px',
                }}
                onClick={onExportClick}
              >
                <FaDownload />
              </button>
            )}
          </div>
        </div>
      )}

      {isLoading ? (
        <Loader color='primary' size='lg' message='' />
      ) : (
        <div style={{overflowX: 'auto', width: '100%'}}>
          <DataTable
            columns={tableHeaders}
            data={data}
            pagination
            onChangePage={(page) => setCurrentPage(page)}
            onChangeRowsPerPage={(newPerPage, page) => {
              setPerPage(newPerPage)
              setCurrentPage(page)
            }}
            // highlightOnHover
            responsive
            // striped
            customStyles={{
              table: {
                style: {
                  width: '100%',
                  tableLayout: 'auto', // This allows natural column sizing
                },
              },
              headCells: {
                style: {
                  color: '#333',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  // textTransform: 'uppercase',
                  padding: '0 5px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              },
              cells: {
                style: {
                  padding: '0 5px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              },
              rows: {
                style: {
                  backgroundColor: '#FFFFFF',
                },
                stripedStyle: {
                  // backgroundColor: 'rgba(153, 170, 255, 0.25)',
                },
              },
            }}
            onColumnOrderChange={handleColumnOrderChange}
          />
        </div>
      )}
      {footerNote && <div className='px-2 pt-2'>{footerNote}</div>}
    </div>
  )
}

export default DataTableList
